import { useContext, useEffect, useState } from 'react';
import { Stack, Collapse } from '@mui/material';
import React from 'react';
import { AppFooter } from 'templates/AppFooter';
import { PageContent } from '../templates/PageContent';
import { AppHeader } from 'templates/AppHeader';
import { FormContentHeader } from 'atoms/FormContentHeader';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { useGetFaq } from 'hooks/u010/useGetFaqsHooks';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AppContext } from 'templates/AppContextProvider';
import { TransitionButton } from 'atoms/TransitionButton';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';
import { ConsentCheck } from 'templates/ConsentCheck';
import { useLazyGetConsentHooks } from 'hooks/U015/useLazyGetConsentHooks';

type FaqData = {
  category: string;
  category_no: number;
  content: {
    faq: string;
    faq_content: string;
    faq_sort_no: number;
    file: string | null;
  }[];
};

export const Faq = () => {
  const [openCategories, setOpenCategories] = useState<{
    [key: number]: boolean;
  }>({});
  const [openFaqs, setOpenFaqs] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();

  const { t: tCommons } = useTranslationWithLang('commons');
  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tConsent } = useTranslationWithLang('consent');

  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const { currentLang } = useContext(AppContext);
  const [data, loading, err] = useGetFaq(currentLang);

  const handleFaqClick = (categoryIndex: number) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex],
    }));
  };

  const handleFaqContentClick = (categoryIndex: number, faqIndex: number) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFaqs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [termOfUseOpen, setTermOfUseOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termOfUseData, termOfUseLoading, termOfUseErr, getTermOfUse] = useLazyGetConsentHooks({
    lang: currentLang,
    consent_type: '0',
  });
  const [privacyNoticeData, privacyNoticeLoading, privacyNoticeErr, getPrivacyNotice] =
    useLazyGetConsentHooks({ lang: currentLang, consent_type: '1' });
  const handleTermOfUseOpen = () => {
    setTermOfUseOpen(true);
    getTermOfUse();
  };
  const handlePrivacyPolicyOpen = () => {
    setPrivacyPolicyOpen(true);
    getPrivacyNotice();
  };

  const showErrorModal = (privacyNoticeErr || termOfUseErr) && !errorDismissed;

  const convertApiResponseToFaqData = (
    apiResponse: {
      category_sort_no: number | null;
      faq_sort_no: number | null;
      title: string | null;
      faq: string | null;
      file1: string | null;
    }[]
  ): FaqData[] => {
    const faqData: FaqData[] = [];

    apiResponse.forEach((item) => {
      if (item.faq_sort_no === 0) {
        const categoryTitle = item.title ?? '';
        faqData.push({
          category: categoryTitle,
          category_no: item.category_sort_no ?? 0,
          content: [],
        });
      }
    });
    apiResponse.forEach((item) => {
      const existingCategory = faqData.find(
        (category) => category.category_no === item.category_sort_no
      );
      if (existingCategory && item.faq_sort_no !== 0) {
        existingCategory.content.push({
          faq: item.title ?? '',
          faq_content: item.faq ?? '',
          faq_sort_no: item.faq_sort_no ?? 0,
          file: item.file1 ?? null,
        });
      }
    });

    faqData.sort((a, b) => (a.category_no ?? 0) - (b.category_no ?? 0));
    faqData.forEach((category) => {
      category.content.sort((a, b) => (a.faq_sort_no ?? 0) - (b.faq_sort_no ?? 0));
    });

    return faqData;
  };

  const faqData = data ? convertApiResponseToFaqData(data.faqs) : [];

  useEffect(() => {
    if (err) {
      setModalMessage(tMessage('MU005F0046E'));
      setShowModal(true);
    }
  }, [err]);

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  return (
    <>
      {loading && <LoadingModal />}
      {showModal && (
        <MessageModal
          message={modalMessage}
          closeLabel={tCommons('TU000_028')}
          onClose={closeModal}
        />
      )}
      {showErrorModal && (
        <MessageModal
          message={tCommons('TU000_058')}
          closeLabel={tConsent('TU015_002')}
          onClose={() => {
            setErrorDismissed(true);
          }}
        />
      )}

      <ConsentCheck
        open={termOfUseOpen}
        onComplete={() => setTermOfUseOpen(false)}
        title={tCommons('TU000_063')}
        consentText={termOfUseData?.consent ?? ''}
        buttonLabel={tCommons('TU000_064')}
        loading={termOfUseLoading}
      />
      <ConsentCheck
        open={privacyPolicyOpen}
        onComplete={() => setPrivacyPolicyOpen(false)}
        title={tCommons('TU000_065')}
        consentText={privacyNoticeData?.consent ?? ''}
        buttonLabel={tCommons('TU000_066')}
        loading={privacyNoticeLoading}
      />
      <AppHeader />
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tCommons('TU000_007')}
      </div>
      <PageContent>
        <FormContentHeader label={tCommons('TU000_007')} />
        <div className="w-full h-full mx-auto text-[--c-text2] rounded-[5px]">
          <Stack spacing={2}>
            <ul className="w-full">
              {faqData.map((item, index) => (
                <li
                  key={index}
                  className="rounded-[5px] border border-[--c-text2] my-2 bg-[--c-background2]"
                >
                  <div
                    onClick={() => handleFaqClick(index)}
                    className="p-2 cursor-pointer"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFaqClick(index);
                    }}
                    role="button"
                    aria-expanded={!!openCategories[index]}
                    aria-controls={`category-content-${index}`}
                  >
                    <h2>{item.category}</h2>
                  </div>
                  <Collapse in={openCategories[index]} timeout="auto" unmountOnExit>
                    <div id={`category-content-${index}`}>
                      <ul className="p-0">
                        {item.content.map((faqItem, faqIndex) => (
                          <li
                            key={faqIndex}
                            className="border border-[--c-text2] rounded-[5px] m-2 bg-[--c-background2]"
                          >
                            <div
                              onClick={() => handleFaqContentClick(index, faqIndex)}
                              className="p-2 cursor-pointer"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleFaqContentClick(index, faqIndex);
                              }}
                              role="button"
                              aria-expanded={!!openFaqs[`${index}-${faqIndex}`]}
                              aria-controls={`faq-content-${index}-${faqIndex}`}
                            >
                              <h3>{faqItem.faq}</h3>
                            </div>
                            <Collapse
                              in={openFaqs[`${index}-${faqIndex}`]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <div className="p-2" id={`faq-content-${index}-${faqIndex}`}>
                                <p className="leading-7" aria-live="polite" role="region">
                                  {faqItem.faq_content.split('\n').map((line, index) => (
                                    <React.Fragment key={index}>
                                      {line}
                                      <br />
                                    </React.Fragment>
                                  ))}
                                </p>
                                {faqItem.file && (
                                  <div aria-live="polite" role="region">
                                    <a
                                      href={faqItem.file}
                                      className="p-2 inline-block text-[-c-text2] underline px-4 mt-2"
                                      tabIndex={0}
                                      title={faqItem.file}
                                      aria-label={tCommons('TU000_075')}
                                    >
                                      {tCommons('TU000_075')}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </Collapse>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Collapse>
                </li>
              ))}
              <li className="rounded-[5px] border border-[--c-text2] my-2 bg-[--c-background2]">
                <div
                  onClick={() => handleTermOfUseOpen()}
                  className="p-2 cursor-pointer"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTermOfUseOpen();
                  }}
                >
                  <h2>{tCommons('TU000_067')}</h2>
                </div>
              </li>
              <li className="rounded-[5px] border border-[--c-text2] my-2 bg-[--c-background2]">
                <div
                  onClick={() => handlePrivacyPolicyOpen()}
                  className="p-2 cursor-pointer"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handlePrivacyPolicyOpen();
                  }}
                >
                  <h2>{tCommons('TU000_070')}</h2>
                </div>
              </li>
              <li className="rounded-[5px] border border-[--c-text2] my-2 bg-[--c-background2]">
                <div
                  onClick={() => navigate(PATHS.INQUIRY)}
                  className="p-2 cursor-pointer"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(PATHS.INQUIRY);
                  }}
                >
                  <h2>{tCommons('TU000_060')}</h2>
                </div>
              </li>
            </ul>
          </Stack>
        </div>
        <div className="flex justify-center items-center">
          <TransitionButton className="w-[10rem]" />
        </div>
      </PageContent>
      <AppFooter />
    </>
  );
};
